import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from './SelectAction.module.scss';
import mineClassNames from '../DeleteTeacherModal.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { TeacherReportItem } from '@/school-management/types/response';
import TheButton from '@/components/school-management/TheButton/TheButton';
import { DeletedTeacherGroupData, GroupTeacher } from '@/school-management/types/response/teacher';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import {
  TeacherDeleteGroupActionRequest,
  TeacherDeleteGroupAction,
} from '@/school-management/types/request/teacher';
import { classService } from '@/school-management/services/class';

interface SelectActionProps {
  teacherId: string;
  schoolId?: string;
  groups: DeletedTeacherGroupData[];
  schoolTeachers: TeacherReportItem[];
  onConfirm: (actions: TeacherDeleteGroupActionRequest[]) => void;
  onCancel: () => void;
}

const DELETE_OPTION_ID = 'delete';
const KEEP_REMAINING_TEACHER_OPTION_ID = 'keepRemainingTeacher';

const SelectAction = ({
  teacherId,
  schoolId,
  groups,
  schoolTeachers,
  onConfirm,
  onCancel,
}: SelectActionProps) => {
  const { t } = useAdminTranslation();
  const [isForAllClasses, setIsForAllClasses] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<{
    [groupId: string]: Option;
  }>({});
  const [groupsWithTeachers, setGroupsWithTeachers] = useState<{
    [groupId: string]: GroupTeacher[];
  }>({});

  // Fetch class details to get teacher information
  useEffect(() => {
    const fetchClassDetails = async () => {
      if (!schoolId) return;

      const groupsToFetch = groups.filter(group => !group.teachers || group.teachers.length === 0);

      if (groupsToFetch.length === 0) {
        // If groups already have teachers, use them
        const teachersMap: { [groupId: string]: GroupTeacher[] } = {};
        groups.forEach(group => {
          if (group.teachers && group.teachers.length > 0) {
            teachersMap[group.groupId] = group.teachers;
          }
        });
        setGroupsWithTeachers(teachersMap);
        return;
      }

      try {
        const teachersMap: { [groupId: string]: GroupTeacher[] } = {};

        // Use existing teachers if available
        groups.forEach(group => {
          if (group.teachers && group.teachers.length > 0) {
            teachersMap[group.groupId] = group.teachers;
          }
        });

        // Fetch missing teacher information
        await Promise.all(
          groupsToFetch.map(async group => {
            try {
              const response = await classService.getClassById(schoolId, group.groupId);
              if (response.data?.status === 'success' && response.data.data?.teachers) {
                teachersMap[group.groupId] = response.data.data.teachers;
              }
            } catch (error) {
              console.error(`Error fetching class details for ${group.groupId}:`, error);
            }
          })
        );

        setGroupsWithTeachers(teachersMap);
      } catch (error) {
        console.error('Error fetching class details:', error);
      }
    };

    fetchClassDetails();
  }, [groups, schoolId]);

  const getActionsOptionsForGroup = useCallback(
    (groupId: string | null): Option[] => {
      if (!groupId) {
        // For "all classes" mode, return standard options
        const teachers = [...schoolTeachers]
          .sort((a, b) => {
            return `${a.firstName} ${a.lastName} ${a.email}`
              .toLowerCase()
              .localeCompare(`${b.firstName} ${b.lastName} ${b.email}`.toLowerCase());
          })
          .filter(t => t.userId !== teacherId)
          .map(t => {
            return {
              label: `${t.firstName} ${t.lastName}`,
              value: t.userId,
              data: t,
            };
          });
        return [
          {
            label: t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.delete'),
            value: DELETE_OPTION_ID,
            data: null,
          },
          ...teachers,
        ];
      }

      const group = groups.find(g => g.groupId === groupId);
      const groupTeachers = groupsWithTeachers[groupId] || group?.teachers || [];

      const remainingTeachers = groupTeachers.filter(t => t.id !== teacherId);
      const hasCoTeachers = remainingTeachers.length >= 1;
      const keptTeacherId = remainingTeachers[0]?.id;

      const teachers = [...schoolTeachers]
        .sort((a, b) => {
          return `${a.firstName} ${a.lastName} ${a.email}`
            .toLowerCase()
            .localeCompare(`${b.firstName} ${b.lastName} ${b.email}`.toLowerCase());
        })
        .filter(t => t.userId !== teacherId && t.userId !== keptTeacherId)
        .map(t => {
          return {
            label: `${t.firstName} ${t.lastName}`,
            value: t.userId,
            data: t,
          };
        });

      const options: Option[] = [
        {
          label: t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.delete'),
          value: DELETE_OPTION_ID,
          data: null,
        },
      ];

      // Add "keep remaining teacher" option if class has co-teachers
      if (hasCoTeachers) {
        const remainingTeacher = remainingTeachers[0];
        if (remainingTeacher) {
          options.push({
            label: t(
              'schoolDetailsPage.classes.deleteTeacherModal.selectAction.keepRemainingTeacher',
              {
                name: `${remainingTeacher.firstName} ${remainingTeacher.lastName}`,
              }
            ),
            value: KEEP_REMAINING_TEACHER_OPTION_ID,
            data: remainingTeacher,
          });
        }
      }

      options.push(...teachers);

      return options;
    },
    [schoolTeachers, teacherId, t, groups, groupsWithTeachers]
  );

  const actionsOptions: Option[] = useMemo(() => {
    const teachers = [...schoolTeachers]
      .sort((a, b) => {
        return `${a.firstName} ${a.lastName} ${a.email || ''}`
          .toLowerCase()
          .localeCompare(`${b.firstName} ${b.lastName} ${b.email || ''}`.toLowerCase());
      })
      .filter(t => t.userId !== teacherId)
      .map(t => {
        return {
          label: `${t.firstName} ${t.lastName}`,
          value: t.userId,
          data: t,
        };
      });
    return [
      {
        label: t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.delete'),
        value: DELETE_OPTION_ID,
        data: null,
      },
      ...teachers,
    ];
  }, [schoolTeachers, teacherId, t]);

  const groupsList = useMemo(() => {
    return groups;
  }, [groups]);

  const canContinue: boolean = useMemo(() => {
    if (isForAllClasses) {
      // All groups must have a selection when "for all classes" is checked
      return groups.every(group => selectedOption[group.groupId] !== undefined);
    }

    return groups.length === Object.keys(selectedOption).length;
  }, [groups, isForAllClasses, selectedOption]);

  const optionRender = useCallback((option: Option) => {
    if (option.value === DELETE_OPTION_ID) {
      return <span className={classNames.deleteOption}>{option.label}</span>;
    }

    if (option.value === KEEP_REMAINING_TEACHER_OPTION_ID) {
      return <span className={classNames.keepRemainingOption}>{option.label}</span>;
    }

    return (
      <div className={classNames.option}>
        <div className={classNames.optionLabel}>{option.label}</div>
        <div className={classNames.optionEmail}>{option.data?.email}</div>
      </div>
    );
  }, []);

  const onActionSelect = useCallback(
    (option: Option, groupId: string | null) => {
      if (!groupId) {
        return;
      }

      if (isForAllClasses) {
        // When "for all classes" is checked, apply the selection to all groups
        const newSelectedOptions: { [groupId: string]: Option } = {};

        groups.forEach(group => {
          const groupTeachers = groupsWithTeachers[group.groupId] || group?.teachers || [];
          const remainingTeachers = groupTeachers.filter(t => t.id !== teacherId);
          const hasCoTeachers = remainingTeachers.length >= 1;

          // Fail safe check
          if (option.value === KEEP_REMAINING_TEACHER_OPTION_ID && !hasCoTeachers) {
            return;
          }

          if (option.value === KEEP_REMAINING_TEACHER_OPTION_ID && hasCoTeachers) {
            const remainingTeacher = remainingTeachers[0];
            if (remainingTeacher && option.data?.id === remainingTeacher.id) {
              newSelectedOptions[group.groupId] = option;
            } else if (remainingTeacher) {
              newSelectedOptions[group.groupId] = {
                label: t(
                  'schoolDetailsPage.classes.deleteTeacherModal.selectAction.keepRemainingTeacher',
                  {
                    name: `${remainingTeacher.firstName} ${remainingTeacher.lastName}`,
                  }
                ),
                value: KEEP_REMAINING_TEACHER_OPTION_ID,
                data: remainingTeacher,
              };
            }
          } else {
            // apply to all groups
            newSelectedOptions[group.groupId] = option;
          }
        });

        setSelectedOption(prevState => ({
          ...prevState,
          ...newSelectedOptions,
        }));
      } else {
        setSelectedOption(prevState => {
          return {
            ...prevState,
            [groupId]: option,
          };
        });
      }
    },
    [isForAllClasses, groups, groupsWithTeachers, teacherId, t]
  );

  const getGroupsSelectedAction = useCallback(
    (groupId: string | null) => {
      if (!groupId) {
        return undefined;
      }

      return selectedOption[groupId];
    },
    [selectedOption]
  );

  const onForAllClassesChange = useCallback((e: any) => {
    const isForAllClasses = e.target.checked;
    setIsForAllClasses(isForAllClasses);
  }, []);

  const onContinue = useCallback(() => {
    const actions = groups.reduce((acc, group) => {
      const groupSelectedAction = selectedOption[group.groupId];

      if (groupSelectedAction?.value === DELETE_OPTION_ID) {
        acc.push({
          groupId: group.groupId,
          replacementTeacherId: '',
          action: TeacherDeleteGroupAction.DeleteGroup,
        });
      } else if (groupSelectedAction?.value === KEEP_REMAINING_TEACHER_OPTION_ID) {
        // We treat kepping the teacher as a replacement of the deleted teacher as we don't have keeping the teacher in the backend ENUM
        const remainingTeacherId = groupSelectedAction?.data?.id;
        if (remainingTeacherId) {
          acc.push({
            groupId: group.groupId,
            replacementTeacherId: remainingTeacherId,
            action: TeacherDeleteGroupAction.ReplaceTeacher,
          });
        } else {
          const groupTeachers = groupsWithTeachers[group.groupId] || group?.teachers || [];
          const remainingTeacher = groupTeachers.find(t => t.id !== teacherId);
          if (remainingTeacher) {
            acc.push({
              groupId: group.groupId,
              replacementTeacherId: remainingTeacher.id,
              action: TeacherDeleteGroupAction.ReplaceTeacher,
            });
          }
        }
      } else {
        acc.push({
          groupId: group.groupId,
          replacementTeacherId: groupSelectedAction?.value || '',
          action: TeacherDeleteGroupAction.ReplaceTeacher,
        });
      }

      return acc;
    }, [] as TeacherDeleteGroupActionRequest[]);

    onConfirm(actions);
  }, [groups, selectedOption, groupsWithTeachers, teacherId, onConfirm]);

  return (
    <div className={classNames.content}>
      <h2>{t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.title')}</h2>
      <span className={classNames.description}>
        {t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.description')}
      </span>
      <div className={classNames.body}>
        <div className={classNames.classes}>
          {groupsList.map((group, index) => {
            return (
              <div key={`group--${index}`} className={classNames.class}>
                <div>
                  {isForAllClasses
                    ? t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.moveAllTo')
                    : t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.to', {
                        name: group.groupName,
                      })}
                </div>
                <div>
                  <DropDown
                    options={getActionsOptionsForGroup(group.groupId)}
                    autoClose={true}
                    optionRender={optionRender}
                    value={getGroupsSelectedAction(group.groupId)}
                    placeholder={t('dropDown.placeholder') || 'Choose'}
                    onChange={(option: Option) => {
                      onActionSelect(option, group.groupId);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className={classNames.allClasses}>
          <label>
            <input type='checkbox' checked={isForAllClasses} onChange={onForAllClassesChange} />
            <span>
              {t('schoolDetailsPage.classes.deleteTeacherModal.selectAction.forAllClasses')}
            </span>
          </label>
        </div>
      </div>
      <div className={mineClassNames.buttonsContainer}>
        <TheButton
          className={mineClassNames.confirmBtn}
          text={t('schoolDetailsPage.classes.deleteTeacherModal.continue')}
          shouldRecolor={false}
          emitOnClick={onContinue}
          disabled={!canContinue}
        />
        <TheButton
          className={mineClassNames.cancelBtn}
          text={t('schoolDetailsPage.classes.deleteTeacherModal.cancel')}
          plain
          shouldRecolor={false}
          emitOnClick={onCancel}
        />
      </div>
    </div>
  );
};

export default SelectAction;
